import { Navigate } from "react-router-dom";

import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  useSidebar,
} from "@/components/ui/sidebar";
import DetailHeaderBar from "components/DetailHeaderBar";
import { SidebarMobileTriggerBar } from "components/sidebar/SidebarMobileTrigger";
import SidebarToggleButton from "components/sidebar/SidebarToggleButton";
import {
  SIDEBAR_CONTENT_CLASSES,
  SIDEBAR_GROUP_LABEL_CLASSES,
  SIDEBAR_MENU_BUTTON_CLASSES,
  SIDEBAR_STYLE,
} from "components/sidebar/sidebarShell";

// The section list is a separate component so it can read the sidebar context
// that SidebarDetailLayout creates and close the mobile sheet after a section is
// selected; otherwise the selected section renders behind the sheet.
const SectionMenu = ({ groups, activeKey, onSelect }) => {
  const { isMobile, setOpenMobile } = useSidebar();

  const selectSection = key => {
    onSelect(key);
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  return (
    <>
      {groups.map((group, index) => (
        <SidebarGroup key={group.label ?? `group-${index}`}>
          {group.label && (
            <SidebarGroupLabel className={SIDEBAR_GROUP_LABEL_CLASSES}>
              {group.label}
            </SidebarGroupLabel>
          )}
          {group.action && (
            <div className="tw:absolute tw:top-2 tw:right-2 tw:z-10 tw:group-data-[collapsible=icon]:hidden">
              {group.action}
            </div>
          )}
          <SidebarMenu>
            {group.items.map(({ key, label, icon: Icon, badge }) => (
              <SidebarMenuItem key={key}>
                <SidebarMenuButton
                  isActive={key === activeKey}
                  onClick={() => selectSection(key)}
                  className={SIDEBAR_MENU_BUTTON_CLASSES}
                  tooltip={label}
                  size="sm"
                >
                  {Icon && <Icon />}
                  <span>{label}</span>
                </SidebarMenuButton>
                {badge && <SidebarMenuBadge>{badge}</SidebarMenuBadge>}
              </SidebarMenuItem>
            ))}
          </SidebarMenu>
          {group.note && (
            <SidebarGroupContent className="tw:px-2 tw:pt-1 tw:group-data-[collapsible=icon]:hidden">
              {group.note}
            </SidebarGroupContent>
          )}
        </SidebarGroup>
      ))}
    </>
  );
};

// Record-detail shell: a section rail beside a pane that shows the section the
// URL names (`tabs` is the page's useAppTabs result). A tab that names no
// visible section redirects to the default section, or the first visible one;
// pass `sectionsLoading` while sections depend on data in flight, so a deep
// link to a late section holds instead of bouncing.
//
// A group's `action` and `note` come from the section that opens it, so set
// them on every section of the group. A `hideFromSidebar` section still
// resolves on its route but is left out of the rail. The collapsed state is
// one cookie shared across detail pages, and Cmd/Ctrl+B toggles both rails.
export default function SidebarDetailLayout({
  sections,
  tabs,
  sectionsLoading = false,
  header = null,
  mobileMenuLabel,
  breadcrumb = null,
  title = null,
  titleActions = null,
  info = null,
  infoLabel = "Details",
  collapsible = "icon",
}) {
  const { activeTab, defaultTab, basePath, handleTabChange } = tabs;
  const visibleSections = sections.filter(section => !section.hidden);

  if (visibleSections.length === 0) {
    return null;
  }

  const activeSection = visibleSections.find(
    section => section.key === activeTab
  );

  if (!activeSection && !sectionsLoading) {
    const target =
      visibleSections.find(section => section.key === defaultTab) ??
      visibleSections[0];
    return <Navigate replace to={`${basePath}/${target.key}`} />;
  }

  const navigationLabel =
    mobileMenuLabel ??
    (typeof header === "string" ? `${header} Navigation` : undefined);

  const menuSections = visibleSections.filter(
    section => !section.hideFromSidebar
  );

  const groups = menuSections.reduce((acc, section) => {
    const label = section.group ?? null;
    const last = acc[acc.length - 1];
    if (last && last.label === label) {
      last.items.push(section);
    } else {
      acc.push({
        label,
        items: [section],
        action: section.groupAction ?? null,
        note: section.groupNote ?? null,
      });
    }
    return acc;
  }, []);

  return (
    <SidebarProvider
      className="tw:h-full tw:min-h-0 tw:min-w-0 tw:flex-1 tw:overflow-hidden tw:[transform:translateZ(0)]"
      style={SIDEBAR_STYLE}
    >
      <Sidebar
        collapsible={collapsible}
        className="tw:shrink-0"
        role="navigation"
        aria-label={navigationLabel ?? "Section navigation"}
      >
        <SidebarHeader className="tw:flex-row tw:items-center tw:justify-between tw:gap-2 tw:group-data-[collapsible=icon]:flex-col tw:group-data-[collapsible=icon]:items-center">
          {typeof header === "string" ? (
            <span className="tw:min-w-0 tw:truncate tw:font-heading tw:text-sm tw:font-semibold tw:text-sidebar-foreground tw:group-data-[collapsible=icon]:hidden">
              {header}
            </span>
          ) : (
            header
          )}
          <SidebarToggleButton />
        </SidebarHeader>
        <SidebarContent className={SIDEBAR_CONTENT_CLASSES}>
          <SectionMenu
            groups={groups}
            activeKey={activeSection?.key}
            onSelect={key => handleTabChange(null, { name: key })}
          />
          {info && (
            <SidebarGroup className="tw:group-data-[collapsible=icon]:hidden">
              <SidebarGroupLabel className={SIDEBAR_GROUP_LABEL_CLASSES}>
                {infoLabel}
              </SidebarGroupLabel>
              <SidebarGroupContent className="tw:flex tw:flex-col tw:gap-4 tw:px-2 tw:pt-1">
                {info}
              </SidebarGroupContent>
            </SidebarGroup>
          )}
        </SidebarContent>
      </Sidebar>
      <div className="tw:flex tw:min-h-0 tw:min-w-0 tw:flex-1 tw:flex-col">
        <SidebarMobileTriggerBar label={navigationLabel} />
        {(breadcrumb || title || titleActions) && (
          <DetailHeaderBar
            breadcrumb={breadcrumb}
            title={title}
            actions={titleActions}
          />
        )}
        <div
          data-slot="detail-pane"
          className="tw:relative tw:flex tw:min-h-0 tw:min-w-0 tw:flex-1 tw:flex-col"
        >
          {/* The gutter stays reserved so content whose height changes, such
              as a filtered Activity Log, does not shift sideways as the
              scrollbar comes and goes. */}
          <div className="tw:flex tw:min-h-0 tw:min-w-0 tw:flex-1 tw:flex-col tw:overflow-y-auto tw:p-4 tw:scrollbar-gutter-stable">
            {activeSection?.content}
          </div>
        </div>
      </div>
    </SidebarProvider>
  );
}
